import { useState, useEffect, useCallback, useRef } from "react";
import { getCompanySettings } from "../services/organizationService";
import { createBreakEntry, getEmployeeTodayBreak } from "../services/breakService";

/**
 * useAutoBreakTracker Hook
 *
 * Tracks sales rep / employee inactivity on the system and automatically
 * triggers break mode if user is inactive beyond the admin-configured threshold.
 * Also tracks daily cumulative break time against the Daily Maximum Break Limit.
 */
export function useAutoBreakTracker({
  isEmployee = false,
  employeeId = null,
  employeeName = "",
  companyCode = "",
}) {
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [isAutoTriggered, setIsAutoTriggered] = useState(false);
  const [breakElapsedSeconds, setBreakElapsedSeconds] = useState(0);
  const [todayTotalBreakSeconds, setTodayTotalBreakSeconds] = useState(0);

  // Settings from admin
  const [autoBreakEnabled, setAutoBreakEnabled] = useState(true);
  const [inactivityTimeoutMinutes, setInactivityTimeoutMinutes] = useState(5);
  const [breakHourLimit, setBreakHourLimit] = useState(60);

  const lastActiveRef = useRef(Date.now());
  const breakStartTimeRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 1. Fetch Company Settings and Today's Initial Break Stats
  useEffect(() => {
    if (!companyCode) return;

    let isSubscribed = true;

    const fetchSettings = async () => {
      try {
        const res = await getCompanySettings(companyCode);
        if (isSubscribed && res?.success && res.settings) {
          if (res.settings.autoBreakEnabled !== undefined) {
            setAutoBreakEnabled(Boolean(res.settings.autoBreakEnabled));
          }
          if (res.settings.inactivityTimeoutMinutes !== undefined) {
            setInactivityTimeoutMinutes(Number(res.settings.inactivityTimeoutMinutes) || 5);
          }
          if (res.settings.breakHourLimit !== undefined) {
            setBreakHourLimit(Number(res.settings.breakHourLimit) || 60);
          }
        }
      } catch (err) {
        console.error("useAutoBreakTracker fetchSettings error:", err);
      }
    };

    const fetchTodayBreak = async () => {
      if (!isEmployee || !employeeId) return;
      try {
        const res = await getEmployeeTodayBreak(companyCode, employeeId);
        if (isSubscribed && res?.success && res.totalSeconds !== undefined) {
          setTodayTotalBreakSeconds(res.totalSeconds);
        }
      } catch (err) {
        console.error("useAutoBreakTracker fetchTodayBreak error:", err);
      }
    };

    fetchSettings();
    fetchTodayBreak();

    return () => {
      isSubscribed = false;
    };
  }, [companyCode, isEmployee, employeeId]);

  // 2. Start Break Handler
  const startBreak = useCallback((manual = true) => {
    breakStartTimeRef.current = Date.now();
    setIsOnBreak(true);
    setIsAutoTriggered(!manual);
    setBreakElapsedSeconds(0);
  }, []);

  // 3. End Break & Log Duration Handler
  const endBreak = useCallback(async () => {
    if (!breakStartTimeRef.current) {
      setIsOnBreak(false);
      setIsAutoTriggered(false);
      setBreakElapsedSeconds(0);
      lastActiveRef.current = Date.now();
      return;
    }

    const durationSeconds = Math.max(1, Math.round((Date.now() - breakStartTimeRef.current) / 1000));
    breakStartTimeRef.current = null;
    setIsOnBreak(false);
    setIsAutoTriggered(false);
    setBreakElapsedSeconds(0);
    lastActiveRef.current = Date.now();

    if (companyCode && employeeId && durationSeconds >= 1) {
      try {
        const res = await createBreakEntry(companyCode, {
          employeeId,
          employeeName: employeeName || "Employee",
          durationSeconds,
        });
        if (isMountedRef.current && res?.success && res.totalSeconds !== undefined) {
          setTodayTotalBreakSeconds(res.totalSeconds);
        }
      } catch (err) {
        console.error("useAutoBreakTracker endBreak log error:", err);
      }
    }
  }, [companyCode, employeeId, employeeName]);

  // 4. Inactivity & Activity Event Listeners
  useEffect(() => {
    if (!isEmployee) return;

    const handleUserActivity = () => {
      if (!isOnBreak) {
        lastActiveRef.current = Date.now();
      } else if (isAutoTriggered) {
        // User came back from inactivity auto-break, resume immediately without modal!
        endBreak();
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, handleUserActivity, { passive: true }));

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handleUserActivity));
    };
  }, [isEmployee, isOnBreak, isAutoTriggered, endBreak]);

  // 5. Inactivity Watcher Interval (Checks every 3 seconds)
  useEffect(() => {
    if (!isEmployee || !autoBreakEnabled || isOnBreak) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const inactiveMs = now - lastActiveRef.current;
      const thresholdMs = Math.max(1, inactivityTimeoutMinutes) * 60 * 1000;

      if (inactiveMs >= thresholdMs) {
        // Auto-trigger break mode
        startBreak(false);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isEmployee, autoBreakEnabled, inactivityTimeoutMinutes, isOnBreak, startBreak]);

  // 6. Active Break Timer Counter (Ticking while on break)
  useEffect(() => {
    if (!isOnBreak) return;

    const timer = setInterval(() => {
      if (breakStartTimeRef.current) {
        const elapsed = Math.max(0, Math.round((Date.now() - breakStartTimeRef.current) / 1000));
        setBreakElapsedSeconds(elapsed);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isOnBreak]);

  return {
    isOnBreak,
    isAutoTriggered,
    breakElapsedSeconds,
    todayTotalBreakSeconds,
    breakHourLimit,
    autoBreakEnabled,
    inactivityTimeoutMinutes,
    startBreak,
    endBreak,
  };
}

export default useAutoBreakTracker;
